import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Mirrors MainTab's 4 stacked step cards (Job Type squares, Questions mode switch,
// Interviewee Voice squares, Mic Check) plus the intro text and the Start button.
function MainTabSkeleton({ cardCls }) {
    return (
        <div className="p-6 md:p-10 max-w-3xl mx-auto space-y-6">
            <div>
                <Skeleton width={320} height={20} />
                <Skeleton width={260} height={13} className="mt-2" />
            </div>

            {/* Step 1 - Job Type */}
            <div className={`${cardCls} p-6`}>
                <Skeleton width={90} height={13} />
                <div className="flex gap-4 justify-center mt-4">
                    <Skeleton width={140} height={140} borderRadius={16} />
                    <Skeleton width={140} height={140} borderRadius={16} />
                </div>
            </div>

            {/* Step 2 - Questions */}
            <div className={`${cardCls} p-6`}>
                <Skeleton width={230} height={13} />
                <div className="flex gap-2 mt-4">
                    <Skeleton width={130} height={36} borderRadius={8} />
                    <Skeleton width={90} height={36} borderRadius={8} />
                </div>
                <Skeleton width="80%" height={13} className="mt-4" />
            </div>

            {/* Step 3 - Interviewee Voice */}
            <div className={`${cardCls} p-6`}>
                <Skeleton width={130} height={13} />
                <div className="flex gap-4 justify-center mt-4">
                    <Skeleton width={110} height={110} borderRadius={16} />
                    <Skeleton width={110} height={110} borderRadius={16} />
                    <Skeleton width={110} height={110} borderRadius={16} />
                </div>
            </div>

            {/* Step 4 - Mic Check */}
            <div className={`${cardCls} p-6`}>
                <Skeleton width={90} height={13} />
                <div className="flex flex-col items-center gap-3 mt-4">
                    <Skeleton circle width={80} height={80} />
                    <Skeleton width={100} height={22} borderRadius={999} />
                    <Skeleton width={190} height={12} borderRadius={999} />
                </div>
            </div>

            <div className="flex justify-center pt-2">
                <Skeleton width={220} height={46} borderRadius={12} />
            </div>
        </div>
    );
}

// Mirrors HistoryTab's column layout (Role, Score, Date, Time Taken, Status) in both the
// desktop grid rows and the mobile stacked-card rows.
function HistoryTabSkeleton({ cardCls, dividerColor }) {
    return (
        <div className="p-4 md:p-6">
            {/* Desktop header + rows */}
            <div
                className="hidden md:grid grid-cols-[1.3fr_0.8fr_1fr_1fr_1fr] gap-4 px-6 py-3 border-b"
                style={{ borderColor: dividerColor }}
            >
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} height={11} width="50%" />
                ))}
            </div>
            {Array.from({ length: 5 }).map((_, i) => (
                <div
                    key={i}
                    className="hidden md:grid grid-cols-[1.3fr_0.8fr_1fr_1fr_1fr] gap-4 items-center px-6 py-4 border-b"
                    style={{ borderColor: dividerColor }}
                >
                    <Skeleton height={16} width="70%" />
                    <Skeleton height={16} width="40%" />
                    <Skeleton height={16} width="60%" />
                    <Skeleton height={16} width="55%" />
                    <Skeleton height={24} width={90} borderRadius={999} />
                </div>
            ))}

            {/* Mobile stacked cards */}
            <div className="md:hidden space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className={`${cardCls} p-4`}>
                        <Skeleton width="55%" height={18} />
                        <div className="mt-3 grid grid-cols-2 gap-y-2 gap-x-3">
                            <Skeleton width="70%" height={13} />
                            <Skeleton width="70%" height={13} />
                            <Skeleton width="70%" height={13} />
                            <Skeleton height={22} width={80} borderRadius={999} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function MockInterviewSkeleton({ isDarkMode, isMainTab = true }) {
    const cardCls = `rounded-2xl border shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`;
    const dividerColor = isDarkMode ? "#334155" : "#e2e8f0";
    const baseColor = isDarkMode ? "#334155" : "#e2e8f0";
    const highlightColor = isDarkMode ? "#475569" : "#f1f5f9";

    return (
        <SkeletonTheme baseColor={baseColor} highlightColor={highlightColor}>
            {isMainTab
                ? <MainTabSkeleton cardCls={cardCls} />
                : <HistoryTabSkeleton cardCls={cardCls} dividerColor={dividerColor} />}
        </SkeletonTheme>
    );
}
